import { apiBasePath } from '../baseUrl';
import { apiUrl } from '../../utils/api';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';

export const extendedApi = apiBasePath.injectEndpoints({
  endpoints: (build) => ({
    getAllSublocations: build.query({
      query: (args) => {
        if (args) {
          const { page = '', perPage = '', searchParams = '', isIr = false } = args;
          return `${apiUrl.sublocation}?perPage=${perPage}&page=${page}&${searchParams}&isIr=${isIr}`;
        }
        return apiUrl.sublocation;
      },
      transformResponse: (response) => response.data.sublocations,
      providesTags: ['SubLocations'],
    }),
    addSubLocation: build.mutation({
      query: (data) => ({
        url: `${apiUrl.sublocation}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SubLocations', 'Locations', 'Location'],
    }),
    updateSubLocationById: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `${apiUrl.sublocation}/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['SubLocations', 'Locations', 'Location', 'Sublocation'],
    }),
    deleteSubLocationById: build.mutation({
      query: (id) => ({
        url: `${apiUrl.sublocation}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubLocations', 'Locations', 'Location'],
    }),
    getSublocationById: build.query({
      query: ({ id = '', apiSearchParams = '' }) => `${apiUrl.sublocation}/${id}?${apiSearchParams}`,
      providesTags: ['Sublocation', 'SubLocations'],
    }),
    getSublocationMachinesById: build.query({
      query: ({ id = '', apiSearchParams = '' }) => `${apiUrl.sublocation}/${id}/assets?${apiSearchParams}`,
      providesTags: ['Sublocation', 'SubLocations'],
    }),
    getSubLocationTypes: build.query({
      query: () => `${apiUrl.sublocation}/${apiUrl.locationType}`,
      transformResponse: (response) => response.data?.sublocationTypes,
    }),
    getAreaTaxLocations: build.query({
      query: (args) => {
        if (args) {
          const { page, perPage, searchParams = '' } = args;
          return `${apiUrl.sublocation}/${apiUrl.areaTaxLocation}?perPage=${perPage || DEFAULT_PER_PAGE}&page=${
            page || DEFAULT_PAGE
          }&${searchParams}`;
        }
        return `${apiUrl.sublocation}/${apiUrl.areaTaxLocation}`;
      },
      transformResponse: (response) => response.data ?? null,
    }),
    updateSubLocationMachineById: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `${apiUrl.sublocation}/${id}/associate-assets`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['Sublocation', 'SubLocations', 'Location'],
    }),
    unlinkMachineFromSublocation: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `${apiUrl.sublocation}/${id}/unlink-assets`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['Sublocation', 'SubLocations', 'Location'],
    }),
    updateSubLocationLeaseById: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `${apiUrl.sublocation}/${id}/${apiUrl.lease}`,
        method: 'PUT',
        body: { ...rest },
      }),
      invalidatesTags: ['Sublocation', 'SubLocations', 'Location'],
    }),
    updateSublocationVendorById: build.mutation({
      query: ({ id, body }) => ({
        url: `${apiUrl.sublocation}/${id}/add-sublocations`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Sublocation', 'SubLocations', 'Location', 'Vendors'],
    }),
    updateSublocationById: build.mutation({
      query: ({ sublocationId, ...rest }) => ({
        url: `${apiUrl.sublocation}/${sublocationId}`,
        method: 'PUT',
        body: { ...rest },
      }),
      invalidatesTags: ['Sublocation', 'SubLocations', 'Location'],
    }),
    getProductCategories: build.query({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.isGLPostingEnabled === true) params.set('IsGLPostingEnabled', 'true');
        if (arg?.isMonthly === true) params.set('isMonthly', 'true');
        const query = params.toString();
        return query ? `${apiUrl.productCategories}?${query}` : `${apiUrl.productCategories}`;
      },
      transformResponse: (response) => response?.data?.productCategories,
    }),
    getGLPostingProductCategories: build.query({
      query: () => `${apiUrl.productCategories}?IsGLPostingEnabled=true`,
      transformResponse: (response) => response?.data?.productCategories,
    }),
  }),
  overrideExisting: false,
});
export const {
  useGetAllSublocationsQuery,
  useLazyGetAllSublocationsQuery,
  useGetSublocationByIdQuery,
  useGetSublocationMachinesByIdQuery,
  useAddSubLocationMutation,
  useUpdateSubLocationByIdMutation,
  useDeleteSubLocationByIdMutation,
  useUpdateSublocationByIdMutation,
  useGetSubLocationTypesQuery,
  useUpdateSublocationVendorByIdMutation,
  useGetAreaTaxLocationsQuery,
  useUpdateSubLocationMachineByIdMutation,
  useUpdateSubLocationLeaseByIdMutation,
  useGetProductCategoriesQuery,
  useLazyGetProductCategoriesQuery,
  useGetGLPostingProductCategoriesQuery,
  useUnlinkMachineFromSublocationMutation,
} = extendedApi;
